import React from 'react';
import { Box, Typography, Card, CardContent, Chip, Button, IconButton, Tooltip } from '@mui/material';
import { AutoAwesome as AutoAwesomeIcon, Settings as SettingsIcon, ViewModule as ViewModuleIcon, Image as ImageIcon, Star as StarIcon, StarBorder as StarBorderIcon } from '@mui/icons-material';
import { ReferenceUnit } from '../../services/api';
import { formatCurrency, formatNumber } from '../../../utils/formatting';

interface ReferenceUnitCardProps {
  referenceUnit: ReferenceUnit | null;
  isExecutiveCondo?: boolean;
  isShortlisted?: boolean;
  shortlistUpdating?: boolean;
  onShortlistClick?: () => void;
  onAISummaryClick?: () => void;
  onSettingsClick?: () => void;
  onInterestClick?: () => void;
  onFloorPlanClick?: (unit: ReferenceUnit) => void;
  onTowerViewClick?: (unit: ReferenceUnit) => void;
}

const ReferenceUnitCard: React.FC<ReferenceUnitCardProps> = ({ 
  referenceUnit, 
  isExecutiveCondo = false,
  isShortlisted = false,
  shortlistUpdating = false,
  onShortlistClick,
  onAISummaryClick,
  onSettingsClick,
  onInterestClick,
  onFloorPlanClick,
  onTowerViewClick,
}) => {
  if (!referenceUnit) {
    return null;
  }

  const npsPrice = referenceUnit.priceNps ?? referenceUnit.price;
  const dpsPrice = referenceUnit.priceDps ?? null;
  const npsPsf = referenceUnit.psfNps ?? referenceUnit.psf;
  const dpsPsf = referenceUnit.psfDps ?? null;
  const showDualPricing = isExecutiveCondo;

  const updatedAt = referenceUnit.unitStatusUpdatedAt
    ? new Date(referenceUnit.unitStatusUpdatedAt).toLocaleString('en-SG', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      })
    : null;

  return (
    <Card
      sx={{
        mb: 3,
        borderRadius: 2,
        bgcolor: 'var(--color-surface)',
        boxShadow: '0 2px 8px rgba(var(--color-shadow-rgb), 0.3)',
      }}
    >
      <CardContent sx={{ p: 3 }}>
        {/* Header with buttons */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              size="small"
              sx={{
                bgcolor: 'var(--color-accent)',
                color: 'var(--color-text-inverse)',
                fontWeight: 600,
                borderRadius: 2,
                px: 2,
                py: 0.75,
                textTransform: 'none',
                '&:hover': {
                  bgcolor: 'var(--color-accent-strong)',
                },
              }}
            >
              Reference Unit
            </Button>
            <Chip
              label={referenceUnit.unitStatus || 'SOLD'}
              size="small"
              sx={{
                bgcolor: 'var(--color-danger)',
                color: 'var(--color-text-inverse)',
                fontWeight: 600,
                height: 28,
                borderRadius: 2,
              }}
            />
            {referenceUnit.region && (
              <Chip
                label={referenceUnit.region}
                size="small"
                sx={{
                  bgcolor: 'rgba(var(--color-accent-rgb), 0.15)',
                  color: 'var(--color-accent)',
                  fontWeight: 600,
                  height: 28,
                  borderRadius: 2,
                }}
              />
            )}
            {updatedAt && (
              <Typography variant="caption" sx={{ color: 'var(--color-accent)', fontWeight: 500 }}>
                (Updated: {updatedAt})
              </Typography>
            )}
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {onAISummaryClick && (
              <Box sx={{ position: 'relative', display: 'inline-block', overflow: 'hidden' }}>
                <Box
                  component="span"
                  sx={{
                    position: 'absolute',
                    top: 6,
                    right: -18,
                    zIndex: 1,
                    px: 2.5,
                    py: 0.35,
                    transform: 'rotate(45deg)',
                    bgcolor: 'var(--color-accent)',
                    color: 'var(--color-text-inverse)',
                    fontWeight: 700,
                    fontSize: '0.45rem',
                    letterSpacing: '0.02em',
                    whiteSpace: 'nowrap',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
                  }}
                >
                  Coming!
                </Box>
                <Button
                  variant="contained"
                  size="small"
                  disabled
                  startIcon={<AutoAwesomeIcon />}
                  onClick={onAISummaryClick}
                  sx={{
                    bgcolor: 'var(--color-accent)',
                    color: 'var(--color-text-inverse)',
                    fontWeight: 600,
                    borderRadius: 2,
                    px: 2,
                    py: 0.75,
                    textTransform: 'none',
                    '&:hover': {
                      bgcolor: 'var(--color-accent-strong)',
                    },
                  }}
                >
                  SMART AI SUMMARY
                </Button>
              </Box>
            )}
            {onSettingsClick && (
              <IconButton
                size="small"
                onClick={onSettingsClick}
                sx={{
                  color: 'var(--color-accent)',
                  '&:hover': {
                    bgcolor: 'rgba(var(--color-accent-rgb), 0.1)',
                  },
                }}
              >
                <SettingsIcon />
              </IconButton>
            )}
          </Box>
        </Box>

        {/* Data in single-row layout with dividers */}
        <Box sx={{ overflowX: { xs: 'auto', md: 'hidden' } }}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '2.2fr 1fr 1.1fr 1fr 1.35fr 1.35fr 1.35fr 1.35fr',
                md: '2.2fr 1fr 1.1fr 1fr 1.35fr 1.35fr 1.35fr 1.35fr',
              },
              minWidth: { xs: 1180, md: '100%' },
              borderRadius: 2,
              border: '1px solid rgba(var(--color-text-rgb), 0.08)',
              overflow: 'hidden',
              backgroundColor: 'var(--color-surface-panel)',
            }}
          >
            <Box sx={{ p: 2 }}>
              <Typography variant="caption" sx={{ display: 'block', mb: 0.5, color: 'rgba(var(--color-text-rgb), 0.7)' }}>
                Unit No.
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 700, color: 'var(--color-text-strong)', fontSize: '1rem' }}>
                {referenceUnit.projectName}
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 700, color: 'var(--color-text-strong)', fontSize: '1.1rem' }}>
                {referenceUnit.unitNumber}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
               
                <Tooltip title="Buyer Interest">
                  <Box
                    onClick={onInterestClick}
                    sx={{
                      display: 'flex',
                      gap: 0.4,
                      cursor: onInterestClick ? 'pointer' : 'default',
                      alignItems: 'center',
                    }}
                  >
                    {[0, 1, 2, 3].map((barIndex) => (
                      <Box
                        key={barIndex}
                        sx={{
                          width: 10,
                          height: 8,
                          borderRadius: 999,
                          bgcolor: barIndex < 3 ? 'var(--color-danger)' : 'var(--color-neutral)',
                        }}
                      />
                    ))}
                  </Box>
                </Tooltip>
                <Tooltip title="Tower View">
                  <IconButton
                    size="small"
                    onClick={() => onTowerViewClick?.(referenceUnit)}
                    sx={{
                      color: 'var(--color-accent)',
                      bgcolor: 'rgba(var(--color-accent-rgb), 0.1)',
                      '&:hover': {
                        bgcolor: 'rgba(var(--color-accent-rgb), 0.2)',
                      },
                    }}
                    aria-label="Tower View"
                  >
                    <ViewModuleIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title={referenceUnit.floorplan ? 'View Floor Plan' : 'No floor plan available'}>
                  <span>
                    <IconButton
                      size="small"
                      onClick={() => onFloorPlanClick?.(referenceUnit)}
                      disabled={!referenceUnit.floorplan}
                      sx={{
                        color: 'var(--color-accent)',
                        bgcolor: 'rgba(var(--color-accent-rgb), 0.1)',
                        '&:hover': {
                          bgcolor: 'rgba(var(--color-accent-rgb), 0.2)',
                        },
                      }}
                      aria-label="Floor Plan"
                    >
                      <ImageIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
                {onShortlistClick && (
                  <Tooltip title={isShortlisted ? 'Remove from shortlist' : 'Add to shortlist'}>
                    <IconButton
                      size="small"
                      onClick={onShortlistClick}
                      disabled={shortlistUpdating}
                      sx={{
                        color: isShortlisted ? 'var(--color-warning-strong)' : 'var(--color-text-strong)',
                        bgcolor: isShortlisted ? 'rgba(var(--color-warning-rgb), 0.15)' : 'rgba(var(--color-text-rgb), 0.08)',
                        '&:hover': {
                          bgcolor: isShortlisted ? 'rgba(var(--color-warning-rgb), 0.25)' : 'rgba(var(--color-text-rgb), 0.12)',
                        },
                        '&.Mui-disabled': { bgcolor: 'rgba(var(--color-text-rgb), 0.05)', color: 'rgba(var(--color-text-rgb), 0.5)' },
                      }}
                      aria-label={isShortlisted ? 'Remove from shortlist' : 'Add to shortlist'}
                    >
                      {isShortlisted ? <StarIcon fontSize="small" /> : <StarBorderIcon fontSize="small" sx={{ color: 'var(--color-text-strong)' }} />}
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
            </Box>

            <Box sx={{ p: 2, borderLeft: '1px solid rgba(var(--color-text-rgb), 0.08)' }}>
              <Typography variant="caption" sx={{ display: 'block', mb: 0.5, color: 'rgba(var(--color-text-rgb), 0.7)' }}>
                Region
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 700, color: 'var(--color-text-strong)' }}>
                {referenceUnit.region || '—'}
              </Typography>
            </Box>

            <Box sx={{ p: 2, borderLeft: '1px solid rgba(var(--color-text-rgb), 0.08)' }}>
              <Typography variant="caption" sx={{ display: 'block', mb: 0.5, color: 'rgba(var(--color-text-rgb), 0.7)' }}>
                Unit Type
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 700, color: 'var(--color-text-strong)' }}>
                {referenceUnit.unitType || '—'}
              </Typography>
            </Box>

            <Box sx={{ p: 2, borderLeft: '1px solid rgba(var(--color-text-rgb), 0.08)' }}>
              <Typography variant="caption" sx={{ display: 'block', mb: 0.5, color: 'rgba(var(--color-text-rgb), 0.7)' }}>
                Type
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 700, color: 'var(--color-text-strong)' }}>
                {referenceUnit.bedroom}-Bed
              </Typography>
              {referenceUnit.unitType && (
                <Typography variant="caption" sx={{ color: 'rgba(var(--color-text-rgb), 0.7)' }}>
                  {referenceUnit.unitType}
                </Typography>
              )}
            </Box>

            <Box sx={{ p: 2, borderLeft: '1px solid rgba(var(--color-text-rgb), 0.08)' }}>
              <Typography variant="caption" sx={{ display: 'block', mb: 0.5, color: 'rgba(var(--color-text-rgb), 0.7)' }}>
                Area
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 700, color: 'var(--color-text-strong)' }}>
                {formatNumber(referenceUnit.areaSqft)} sqft
              </Typography>
            </Box>

            <Box sx={{ p: 2, borderLeft: '1px solid rgba(var(--color-text-rgb), 0.08)' }}>
              <Typography variant="caption" sx={{ display: 'block', mb: 0.5, color: 'rgba(var(--color-text-rgb), 0.7)' }}>
                {showDualPricing ? 'NPS' : 'Price'}
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 700, color: 'var(--color-text-strong)', mb: 0.25 }}>
                {formatCurrency(npsPrice)}
              </Typography>
              <Typography variant="caption" sx={{ color: 'rgba(var(--color-text-rgb), 0.7)' }}>
                {showDualPricing ? 'NPS PSF' : 'PSF'} {npsPsf ? formatCurrency(npsPsf) : '—'}
              </Typography>
              {showDualPricing && (
                <>
                  <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: 'rgba(var(--color-text-rgb), 0.7)' }}>
                    DPS {dpsPrice ? formatCurrency(dpsPrice) : '—'}
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'rgba(var(--color-text-rgb), 0.7)' }}>
                    DPS PSF {dpsPsf ? formatCurrency(dpsPsf) : '—'}
                  </Typography>
                </>
              )}
            </Box>

            <Box sx={{ p: 2, borderLeft: '1px solid rgba(var(--color-text-rgb), 0.08)' }}>
              <Typography variant="caption" sx={{ display: 'block', mb: 0.5, color: 'rgba(var(--color-text-rgb), 0.7)' }}>
                Estimated Total Outlay
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 700, color: 'var(--color-text-strong)' }}>
                {formatCurrency(referenceUnit.outlay)}
              </Typography>
            </Box>

            <Box sx={{ p: 2, borderLeft: '1px solid rgba(var(--color-text-rgb), 0.08)' }}>
              <Typography variant="caption" sx={{ display: 'block', mb: 0.5, color: 'rgba(var(--color-text-rgb), 0.7)' }}>
                Estimated Monthly Installment
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 700, color: 'var(--color-text-strong)' }}>
                {formatCurrency(referenceUnit.installment)}
              </Typography>
            </Box>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

export default ReferenceUnitCard;
